import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import { LOCAL_TIME_24_HOUR_FORMAT } from '../services/Constant';

export interface TimeDuration {
  hour: number;
  minutes: number;
}

export const formatDate = (newDate: Date, format: string) =>
  dayjs(newDate).format(format);

export const formatStringTime = (time: string) => {
  const timeArray = time.split(':');
  return parseInt(timeArray[0], 10) * 60 * 60 + parseInt(timeArray[1], 10) * 60;
};

export const formatTime = (time: number): string => {
  const h = Math.floor(time / 3600)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((time % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${h}:${m}`;
};

export const getTimeString = (
  dateTime: string,
  format: string,
  utc: boolean,
): string => {
  if (dateTime.length === 0) {
    return '';
  }
  return moment(dateTime).utc(utc).local().format(format);
};

export const getAppStartTime = (
  time: string | null,
  format: string,
  utc: boolean,
) => {
  if (time === null) {
    return '';
  }
  return getTimeString(time, format, utc);
};

export const chronologicalAgeLogic = (
  DOB: Date | null | undefined,
  DOA: Date | null | undefined,
) => {
  let cronAge = '';
  let totalMonths: number = 0;
  let days: number = 0;
  //Get the start date
  if (DOB && DOA) {
    const startDate = new Date(DOB);

    // Get the current date
    const currentDate = new Date(DOA);

    if (startDate <= currentDate) {
      let years = currentDate.getFullYear() - startDate.getFullYear();
      let months = currentDate.getMonth() - startDate.getMonth();
      days = currentDate.getDate() - startDate.getDate();

      if (days < 0) {
        months--;
        const tempDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - 1,
          startDate.getDate(),
        );
        const lastMonthDate = new Date(
          tempDate.getFullYear(),
          tempDate.getMonth() + 1,
          0,
        ).getDate();
        days += lastMonthDate;
      }

      // Check if the current month is earlier than the birth month
      if (months < 0) {
        years--;
        months += 12;
      }

      totalMonths = years * 12 + months;

      cronAge = `${years}yr(s) ${months}m ${days}d (${totalMonths} Month(s))`;
    } else if (
      startDate > currentDate ||
      (startDate > currentDate && currentDate)
    ) {
      cronAge = '';
    }
  } else {
    cronAge = '';
  }
  return { cronAge, totalMonths, days };
};
export const checkTimeExceeding = (time: string, maxTime: string) => {
  const [hours, minutes] = maxTime.split(':');
  const maxTimeInSeconds = +hours * 60 * 60 + +minutes * 60;

  const [newHours, newMinutes] = time.split(':');
  const timeInSeconds = +newHours * 60 * 60 + +newMinutes * 60;
  return maxTimeInSeconds < timeInSeconds;
};

export const getFinancialYear = (selectedYear: string): string => {
  const years = selectedYear.split('-').map(Number);
  const previousYear = years[0];

  const currentMonth = new Date().getMonth() + 1;

  const financialYearStartDate = new Date(previousYear, 6, 1);
  const financialYearEndDate = new Date(previousYear + 1, 5, 30);

  if (currentMonth >= 7) {
    return `${dayjs(financialYearStartDate).format('MMM DD, YYYY')} - ${dayjs(
      financialYearEndDate,
    ).format('MMM DD, YYYY')}`;
  }

  return `${dayjs(financialYearStartDate).format('MMM DD, YYYY')} - ${dayjs(
    financialYearEndDate,
  ).format('MMM DD, YYYY')}`;
};

export const getFinancialYearJuneEndDate = (
  selectedYear: string,
): dayjs.Dayjs => {
  const years = selectedYear.split('-').map(Number);
  const fiscalYearCurrentMonth = dayjs().set('year', years[1]);
  return fiscalYearCurrentMonth.month(5).endOf('month');
};

export const getFinancialYearJulyFirstDate = (
  selectedYear: string,
): dayjs.Dayjs => {
  const years = selectedYear.split('-').map(Number);
  const fiscalYearCurrentMonth = dayjs().set('year', years[0]);
  return fiscalYearCurrentMonth.month(6).startOf('month');
};

export const calculateMonths = (dob: string) => {
  const [month, day, year] = dob.split('/').map(Number);

  const birth = new Date(year, month - 1, day);
  const today = new Date();

  birth.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (birth > today) {
    throw new Error('DOB cannot be in the future');
  }

  // Step 1: calculate total months difference
  let totalMonths =
    (today.getFullYear() - birth.getFullYear()) * 12 +
    (today.getMonth() - birth.getMonth());

  // If current day is before birth day → last month not completed
  if (today.getDate() < birth.getDate()) {
    totalMonths--;
  }

  // Step 2: calculate remaining days from last month anniversary
  const lastMonthDate = new Date(birth);
  lastMonthDate.setMonth(lastMonthDate.getMonth() + totalMonths);

  // Handle 29/30/31 overflow safely
  if (lastMonthDate.getDate() !== birth.getDate()) {
    lastMonthDate.setDate(0);
  }

  const remainingDays = Math.floor(
    (today.getTime() - lastMonthDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  return `${totalMonths} M (${remainingDays} Days)`;
};

const formatNegative = (num: number) => {
  const sign = num < 0 || Object.is(num, -0) ? '-' : '';
  const absValue = Math.abs(num);
  return sign + String(absValue).padStart(2, '0');
};

export const formatTimeDurationInDecimals = (
  duration: TimeDuration,
  separator: string,
) => {
  const hourStr =
    duration.hour <= 0
      ? formatNegative(duration.hour)
      : duration.hour.toString().padStart(2, '0');
  return `${hourStr}${separator}${duration.minutes
    .toString()
    .padStart(2, '0')}`;
};

export const formatStringDate = (time: string | null): TimeDuration => {
  if (time === null || time.length === 0) {
    return {
      hour: 0,
      minutes: 0,
    };
  }
  return {
    hour: Number(time.split(':')[0]),
    minutes: Number(time.split(':')[1]),
  };
};

export const formatStringDateInDecimals = (
  time: string | null,
): TimeDuration => {
  if (time === null || time.length === 0) {
    return {
      hour: 0,
      minutes: 0,
    };
  } else if (time.includes('.')) {
    return {
      hour: Number(time.split('.')[0]),
      minutes: Number(time.split('.')[1]),
    };
  } else {
    return {
      hour: Number(time.split(':')[0]),
      minutes: Number(time.split(':')[1]),
    };
  }
};

export const formatToDurationFromDecimal = (duration: number): TimeDuration => {
  return {
    hour: Math.floor(duration),
    minutes: Math.round((duration - Math.floor(duration)) * 100),
  };
};

export const formatToDecimalFromDuration = (duration: TimeDuration): number => {
  return duration.hour + duration.minutes / 100;
};

export const convertTimeDuration = (
  time: string | null,
  isDecimal: boolean,
) => {
  if (time === null) {
    return isDecimal ? '0.00' : '00:00';
  } else {
    const isNegative = time.startsWith('-');
    const timeValue = isNegative ? time.slice(1) : time;
    if (isDecimal) {
      const hours = parseInt(timeValue.split(':')[0], 10)
        .toString()
        .padStart(2, '0');
      const minutes = timeValue.split(':')[1];
      let minutesConverted = '00';
      if (minutes === '00') {
        minutesConverted = '00';
      } else if (minutes === '15') {
        minutesConverted = '25';
      } else if (minutes === '30') {
        minutesConverted = '50';
      } else if (minutes === '45') {
        minutesConverted = '75';
      }
      return `${isNegative ? '-' : ''}${hours.padStart(
        2,
        '0',
      )}.${minutesConverted.padStart(2, '0')}`;
    } else {
      const hours = timeValue.split('.')[0];
      const minutes = timeValue.split('.')[1];
      let minutesConverted = '00';
      if (minutes === '25') {
        minutesConverted = '15';
      } else if (minutes === '50') {
        minutesConverted = '30';
      } else if (minutes === '75') {
        minutesConverted = '45';
      }

      return `${isNegative ? '-' : ''}${hours.padStart(
        2,
        '0',
      )}:${minutesConverted}`;
    }
  }
};

// export const threeYearsAgo = () => {
//   const currentDate = new Date();
//   const threeYears = new Date(currentDate);
//   threeYears.setFullYear(currentDate.getFullYear() - 3);
//   return threeYears;
// };

export const threeYearsAgo = () => {
  const currentDate = dayjs();
  const threeYearsAgoDate = currentDate.subtract(3, 'year');
  return threeYearsAgoDate;
};

export const getTime = (time: string | null, defaultValue: string) => {
  if (time === null) {
    return dayjs(defaultValue, LOCAL_TIME_24_HOUR_FORMAT);
  }
  return dayjs(time);
};

export const datePickerMinDateMaxDateValidate = (
  value: Dayjs | null | string | Date | undefined,
) => {
  const pickerMin = '01/01/1900';
  const pickerMax = '12/31/2099';
  if (dayjs(value).isBefore(pickerMin) || dayjs(value).isAfter(pickerMax)) {
    return true;
  }
};

export const timeToMinutes = (timeString: string): number => {
  const isNegative = timeString.startsWith('-');
  const timeValue = isNegative ? timeString.slice(1) : timeString;
  const [hours, minutes] = timeValue.split(':').map(Number); // Split and convert to numbers
  return isNegative ? -(hours * 60 + minutes) : hours * 60 + minutes; // Convert hours to minutes and add the minutes
};

export const minutesToTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60); // Get the integer part for hours
  const minutes = totalMinutes % 60; // Get the remaining minutes
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}`; // Format as "HH:mm"
};

export const getFiscalYearInfo = (date: Date) => {
  // Uses local time for fiscal year calculation (July 1 - June 30)
  const year = date.getFullYear();
  const month = date.getMonth();

  let startYear, endYear;
  if (month >= 6) {
    // July or later
    startYear = year;
    endYear = year + 1;
  } else {
    // Before July
    startYear = year - 1;
    endYear = year;
  }

  // Generate fiscal years array (current year and 3 previous years)
  const fiscalYears: string[] = [];
  const earliestStartYear = startYear - 2;
  for (let i = 0; i < 4; i++) {
    const fiscalStartYear = earliestStartYear + i;
    fiscalYears.push(`${fiscalStartYear} - ${fiscalStartYear + 1}`);
  }

  return {
    fiscalYears,
    startYear,
    endYear,
  };
};

export const calculateDuration = (
  startDate: string,
  endDate: string,
): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  const hours = Math.floor(diffMins / 60);
  const minutes = diffMins % 60;

  return `${hours.toString().padStart(2, '0')} Hr ${minutes
    .toString()
    .padStart(2, '0')} mins`;
};

export const convertUTCtoLocalTimezone = (
  date: string,
  actualFormat: string,
  requiredFormat: string,
) => {
  return moment(date, actualFormat).utc().local().format(requiredFormat);
};

export const convertToHHMM = (duration: string | null | undefined) => {
  const value = Number(duration);

  if (Number.isNaN(value) || value < 0) return '00:00';

  let hours = Math.floor(value);
  let minutes = Math.round((value - hours) * 60);

  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');

  return `${hh}:${mm}`;
};

export const convertEndDatetoUtcForHours = (
  date: string | null | undefined,
  time: string | null | undefined,
  duration: string | null | undefined,
) => {
  const start = moment(`${date} ${time}`, 'MM/DD/YYYY HH:mm');

  if (!duration) return null;

  const [durHours, durMinutes] = duration.split(':').map(Number);

  const end = start.add(durHours || 0, 'hours').add(durMinutes || 0, 'minutes');
  return end.utc().format('YYYY-MM-DDTHH:mm:ss');
};

export const getEndDateTime = (
  startDateTime: string | null | undefined,
  duration: string | null | undefined,
) => {
  if (!startDateTime || !duration) return null;

  const start = moment(startDateTime);

  const [hours, minutes] = duration.split(':').map(Number);

  const end = start.add(hours || 0, 'hours').add(minutes || 0, 'minutes');

  return end.format('YYYY-MM-DDTHH:mm:ss');
};
